import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";
import { TrendingUp, TrendingDown, RefreshCw, ArrowUpRight, ArrowDownRight, Globe, Coins, DollarSign, BarChart3 } from "lucide-react";
import { API } from "../App";

const MarketDashboard = () => {
  const [activeTab, setActiveTab] = useState("crypto");
  const [marketData, setMarketData] = useState({
    crypto: [],
    stocks: [],
    forex: [],
    commodities: []
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/market/all`);
      setMarketData({
        crypto: response.data.crypto || [],
        stocks: response.data.stocks || [],
        forex: response.data.forex || [],
        commodities: response.data.commodities || []
      });
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching market data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);
  
  const tabs = [
    { id: "crypto", label: "Crypto", icon: <Coins size={18} /> },
    { id: "stocks", label: "Stocks", icon: <BarChart3 size={18} /> },
    { id: "forex", label: "Forex", icon: <Globe size={18} /> },
    { id: "commodities", label: "Commodities", icon: <DollarSign size={18} /> }
  ];
  
  const generateMockChart = () => {
    return Array.from({ length: 24 }, (_, i) => ({
      time: `${i}:00`,
      value: 100 + Math.random() * 20 - 10 + (i * 0.5)
    }));
  };
  
  const currentData = marketData[activeTab] || [];
  
  // Market summary calculations
  const gainers = currentData.filter(item => item.change_24h > 0).length;
  const losers = currentData.filter(item => item.change_24h < 0).length;
  const avgChange = currentData.length > 0 
    ? (currentData.reduce((sum, item) => sum + (item.change_24h || 0), 0) / currentData.length).toFixed(2)
    : 0;
  
  return (
    <div className="market-dashboard" data-testid="market-dashboard">
      <div className="container">
        {/* Header */}
        <div className="dashboard-header" data-testid="dashboard-header">
          <div>
            <h1>Market Dashboard</h1>
            <p>Real-time market data across all asset classes</p>
          </div>
          <button 
            className="refresh-btn" 
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
        
        {lastUpdate && (
          <div className="last-update" data-testid="last-update">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </div>
        )}
        
        {/* Market Summary */}
        <div className="market-summary" data-testid="market-summary">
          <div className="summary-card">
            <span className="summary-label">Total Assets</span>
            <span className="summary-value">{currentData.length}</span>
          </div>
          <div className="summary-card gainers">
            <span className="summary-label">Gainers</span>
            <span className="summary-value">
              <TrendingUp size={18} />
              {gainers}
            </span>
          </div>
          <div className="summary-card losers">
            <span className="summary-label">Losers</span>
            <span className="summary-value">
              <TrendingDown size={18} />
              {losers}
            </span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Avg. Change</span>
            <span className={`summary-value ${avgChange >= 0 ? 'positive' : 'negative'}`}>
              {avgChange >= 0 ? '+' : ''}{avgChange}%
            </span>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="tabs" data-testid="market-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Market Table */}
        <div className="market-table-container" data-testid="market-table-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading market data...</p>
            </div>
          ) : (
            <table className="market-table" data-testid="market-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Asset</th>
                  <th>Price</th>
                  <th>24h Change</th>
                  <th>24h Chart</th>
                  {activeTab === 'crypto' && <th>Market Cap</th>}
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => (
                  <motion.tr
                    key={item.id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`market-row-${index}`}
                  >
                    <td className="rank">{index + 1}</td>
                    <td>
                      <div className="asset-info">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="asset-icon" />
                        ) : (
                          <div className="asset-icon-placeholder">{item.symbol.charAt(0)}</div>
                        )}
                        <div>
                          <span className="symbol">{item.symbol}</span>
                          <span className="name">{item.name}</span>
                        </div>
                      </div>
                    </td>
                    <td className="price mono">
                      ${item.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: item.price > 1 ? 2 : 6 })}
                    </td>
                    <td className={`change mono ${item.change_24h >= 0 ? 'positive' : 'negative'}`}>
                      <span className="change-indicator">
                        {item.change_24h >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                        {Math.abs(item.change_24h || 0).toFixed(2)}%
                      </span>
                    </td>
                    <td className="mini-chart">
                      <ResponsiveContainer width={100} height={40}>
                        <AreaChart data={generateMockChart()}>
                          <defs>
                            <linearGradient id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={item.change_24h >= 0 ? '#00FF94' : '#FF3B30'} stopOpacity={0.3} />
                              <stop offset="100%" stopColor={item.change_24h >= 0 ? '#00FF94' : '#FF3B30'} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke={item.change_24h >= 0 ? '#00FF94' : '#FF3B30'}
                            fill={`url(#gradient-${index})`}
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </td>
                    {activeTab === 'crypto' && (
                      <td className="market-cap mono">
                        ${item.market_cap?.toLocaleString() || '-'}
                      </td>
                    )}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Info Card */}
        <div className="info-card card" data-testid="info-card">
          <h3>About {tabs.find(t => t.id === activeTab)?.label} Markets</h3>
          {activeTab === 'crypto' && (
            <p>Cryptocurrency markets operate 24/7 worldwide. Prices are highly volatile and can change rapidly. Data provided by CoinGecko.</p>
          )}
          {activeTab === 'stocks' && (
            <p>Stock market data shows major US equities. Markets are open Mon-Fri, 9:30 AM - 4:00 PM ET. Data is simulated for educational purposes.</p>
          )}
          {activeTab === 'forex' && (
            <p>Foreign exchange (Forex) markets are the largest financial markets in the world. They operate 24/5. Data is simulated for educational purposes.</p>
          )}
          {activeTab === 'commodities' && (
            <p>Commodities include precious metals like gold and silver, energy products, and agricultural goods. Data is simulated for educational purposes.</p>
          )}
        </div>
      </div>
      
      <style>{`
        .market-dashboard {
          padding: 40px 0 80px;
        }
        
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        
        .dashboard-header h1 {
          font-size: 2.5rem;
          margin-bottom: 8px;
        }
        
        .dashboard-header p {
          color: var(--text-secondary);
        }
        
        .refresh-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          cursor: pointer;
          transition: border-color 0.2s;
        }
        
        .refresh-btn:hover {
          border-color: var(--primary);
        }
        
        .refresh-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .refresh-btn .spinning {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .last-update {
          font-size: 13px;
          color: var(--text-muted);
          margin-bottom: 24px;
        }
        
        /* Market Summary */
        .market-summary {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }
        
        .summary-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .summary-label {
          font-size: 13px;
          color: var(--text-muted);
        }
        
        .summary-value {
          font-family: var(--font-mono);
          font-size: 1.5rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .summary-card.gainers .summary-value {
          color: var(--success);
        }
        
        .summary-card.losers .summary-value {
          color: var(--error);
        }
        
        .summary-value.positive { color: var(--success); }
        .summary-value.negative { color: var(--error); }
        
        /* Tabs */
        .tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          background: var(--bg-subtle);
          padding: 6px;
          border-radius: 12px;
          width: fit-content;
        }
        
        .tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: var(--text-secondary);
          font-weight: 500;
          cursor: pointer;
          transition: color 0.2s, background-color 0.2s;
        }
        
        .tab:hover {
          color: var(--text-primary);
        }
        
        .tab.active {
          background: var(--bg-paper);
          color: var(--text-primary);
        }
        
        /* Table */
        .market-table-container {
          background: var(--bg-paper);
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 32px;
        }
        
        .market-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .market-table th {
          text-align: left;
          padding: 16px 20px;
          font-family: var(--font-heading);
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-muted);
          background: var(--bg-subtle);
        }
        
        .market-table td {
          padding: 16px 20px;
          border-top: 1px solid var(--border);
        }
        
        .market-table tr:hover td {
          background: rgba(255, 255, 255, 0.02);
        }
        
        .rank {
          font-family: var(--font-mono);
          color: var(--text-muted);
          width: 50px;
        }
        
        .asset-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .asset-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
        }
        
        .asset-icon-placeholder {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--primary);
          border-radius: 8px;
          font-weight: 700;
          font-size: 14px;
        }
        
        .asset-info .symbol {
          display: block;
          font-family: var(--font-mono);
          font-weight: 600;
          font-size: 14px;
        }
        
        .asset-info .name {
          display: block;
          font-size: 12px;
          color: var(--text-muted);
        }
        
        .mono {
          font-family: var(--font-mono);
        }
        
        .price {
          font-weight: 500;
        }
        
        .change {
          font-weight: 500;
        }
        
        .change.positive { color: var(--success); }
        .change.negative { color: var(--error); }
        
        .change-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .market-cap {
          color: var(--text-secondary);
        }
        
        .mini-chart {
          padding: 8px 16px !important;
        }
        
        /* Loading */
        .loading-state {
          text-align: center;
          padding: 60px;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }
        
        /* Info Card */
        .info-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
        }
        
        .info-card h3 {
          margin-bottom: 12px;
        }
        
        .info-card p {
          color: var(--text-secondary);
          font-size: 14px;
          line-height: 1.6;
        }
        
        @media (max-width: 1024px) {
          .market-summary {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .tabs {
            width: 100%;
            overflow-x: auto;
          }
        }
        
        @media (max-width: 768px) {
          .dashboard-header {
            flex-direction: column;
            gap: 16px;
          }
          
          .market-summary {
            grid-template-columns: 1fr;
          }
          
          .market-table th:nth-child(5),
          .market-table td:nth-child(5),
          .market-table th:nth-child(6),
          .market-table td:nth-child(6) {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default MarketDashboard;